import { initializeApp, getApps } from 'firebase/app';
import {
  getAnalytics,
  isSupported,
  logEvent,
  setAnalyticsCollectionEnabled,
  type Analytics,
} from 'firebase/analytics';

export const CONSENT_KEY = 'wizantix_cookie_consent';

type ConsentChoice = 'accepted' | 'declined';
type ConsentFallbackSource = 'session' | 'storage';

interface ConsentFallbackState {
  choice: ConsentChoice | null;
  source: ConsentFallbackSource;
}

declare global {
  interface Window {
    __wizantixConsentFallback?: ConsentFallbackState;
  }
}

interface AnalyticsEventParams {
  [key: string]: string | number | boolean | undefined;
}

const DEFAULT_FIREBASE_CONFIG = {
  apiKey: 'AIzaSyB_TTahhPNDv2EgvMQ4HxfpS5cAFC7UIEo',
  authDomain: 'wizantix.firebaseapp.com',
  projectId: 'wizantix',
  storageBucket: 'wizantix.firebasestorage.app',
  messagingSenderId: '615518649450',
  appId: '1:615518649450:web:c5e8802654b21fb771de13',
  measurementId: 'G-6WGZ5BCZMY',
};

const GAMES_ANALYTICS_LABELS = {
  site_key: 'wizantix_games',
  site_domain: 'wizantix.games',
  site_product: 'games',
  app_version: __APP_VERSION__,
} as const;

const getConsentFallback = (): ConsentFallbackState | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.__wizantixConsentFallback ?? null;
};

const storeConsentFallback = (
  choice: ConsentChoice | null,
  source: ConsentFallbackSource
): void => {
  if (typeof window === 'undefined') {
    return;
  }

  window.__wizantixConsentFallback = { choice, source };
};

const getStoredConsent = (): ConsentChoice | null => {
  try {
    const value = localStorage.getItem(CONSENT_KEY);
    if (value === 'accepted' || value === 'declined') {
      storeConsentFallback(value, 'storage');
      return value;
    }

    const fallback = getConsentFallback();
    if (fallback?.source === 'session') {
      return fallback.choice;
    }

    storeConsentFallback(null, 'storage');
    return null;
  } catch {
    return getConsentFallback()?.choice ?? null;
  }
};

export const setConsentFallback = (choice: ConsentChoice | null): void => {
  storeConsentFallback(choice, 'session');
};

const getFirebaseConfig = () => {
  const envConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
  };

  const hasEnvOverride =
    !!envConfig.apiKey && !!envConfig.projectId && !!envConfig.appId && !!envConfig.measurementId;

  if (hasEnvOverride) {
    return envConfig;
  }

  return DEFAULT_FIREBASE_CONFIG;
};

export const normalizeAnalyticsTarget = (value: string, fallback: string): string => {
  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s_-]/g, '')
    .replace(/\s+/g, '_')
    .slice(0, 64);

  return normalized || fallback;
};

export const initFirebaseAnalytics = async (): Promise<Analytics | null> => {
  if (typeof window === 'undefined') {
    return null;
  }

  const consent = getStoredConsent();
  if (consent !== 'accepted') {
    return null;
  }

  const config = getFirebaseConfig();
  if (!config) {
    return null;
  }

  if (!(await isSupported())) {
    return null;
  }

  const app = getApps()[0] ?? initializeApp(config);
  const analytics = getAnalytics(app);

  setAnalyticsCollectionEnabled(analytics, true);

  return analytics;
};

export const setAnalyticsConsent = (
  analytics: Analytics | null,
  choice: ConsentChoice
): void => {
  if (!analytics) {
    return;
  }

  setAnalyticsCollectionEnabled(analytics, choice === 'accepted');
};

export const trackAnalyticsEvent = (
  analytics: Analytics | null,
  eventName: string,
  params?: AnalyticsEventParams
): void => {
  if (!analytics) {
    return;
  }
  logEvent(analytics, eventName, {
    ...GAMES_ANALYTICS_LABELS,
    ...params,
  });
};
